/**
 * FMV formula config — RUF-441.
 *
 * The version-controlled source of truth for the *entire* FMV formula: age
 * curves, condition multipliers, marketability tiers/factors, friction rule
 * parameters, and the telemetry factor (composed from
 * telemetry-multiplier.config.ts). Generalizes the RUF-447/457 telemetry
 * pattern to every factor `computeFmv` (fmv-kernel.ts) consumes.
 *
 * ## Source of truth (ADR-0006)
 *
 * These values were the source of truth in the DB reference tables
 * (age_curve / condition_multiplier / marketability_assignment /
 * friction_cost_rule, see db/schema/090_fmv_config.sql). As of RUF-441 the
 * config file below is authoritative; those tables are retained physically but
 * no longer read (deprecated; a follow-up drops them). Formula history is now
 * `git log -p` on this file, not deltas scattered across migrations.
 *
 * `itad_rate` and `oem_floor` are deliberately NOT migrated here — they feed
 * itad_baseline_usd / oem_floor_usd, not the multiplier chain, and stay
 * DB-backed for now (see ADR-0006 "deferred").
 *
 * ## Fidelity
 *
 * The default below reproduces what the active `v1.0` model version resolved in
 * the DB (db/seed/prototype-seed.sql), so recompute output is byte-identical for
 * unchanged config. See __tests__/fmv-formula-config-parity.test.ts.
 *
 * ## Versioning
 *
 * `version` is a deterministic content hash of every field below (see
 * config-hash.ts) — editing any multiplier / factor / rule produces a new
 * `version` with no code change. Persist it into
 * fmv_pricing_snapshot.formula_config_version alongside every computed price.
 */

import type { ConditionBand, MarketabilityTier } from '../db.js';
import { askCalibrationConfigViolation, type AskCalibrationConfig } from './ask-calibration.js';
import { hashConfig } from './config-hash.js';
import type { TelemetryMultiplierConfig } from './telemetry-multiplier.config.js';
import { defaultTelemetryMultiplierConfig } from './telemetry-multiplier.config.js';

/** asset_class.id — free text (e.g. 'gpu', 'server', 'ib_switch'). */
type AssetClassId = string;

/** One age bracket → multiplier band. Mirrors an age_curve row. */
export interface AgeBand {
  /** Inclusive lower bound, months. */
  ageMonthsMin: number;
  /** Exclusive upper bound, months. */
  ageMonthsMax: number;
  multiplier: number;
}

/** Condition-band multiplier band. Mirrors a condition_multiplier row. */
export interface ConditionMultiplierBand {
  low: number;
  default: number;
  high: number;
}

/** A marketability tier + its numeric factor (class default or SKU override). */
export interface MarketabilityValue {
  tier: MarketabilityTier;
  factor: number;
}

export interface MarketabilityForClass {
  classDefault: MarketabilityValue;
  /** SKU-level overrides keyed by sku_id; a SKU present here wins over classDefault. */
  skuOverrides?: Record<string, MarketabilityValue>;
}

/**
 * A friction cost rule. Mirrors the subset of friction_cost_rule the current
 * resolver actually uses: (component, region) → per-unit + fixed USD. Lot-size
 * brackets are intentionally omitted — `resolveFrictionBootstrap` never read
 * them, and normal recomputes carry friction forward rather than re-deriving
 * (see ADR-0006 / RUF-441 plan §3).
 */
export interface FrictionRule {
  componentId: string;
  /** null = region-agnostic. The current resolver prefers the region-agnostic rule. */
  regionId: string | null;
  perUnitUsd: number;
  fixedUsd: number;
}

/**
 * Composite (sum-of-parts) roll-up config for a PARENT asset class whose FMV is
 * derived from its children's already-computed FMVs rather than from a SKU
 * baseline — SIL-1149 (docs/superpowers/specs/2026-09-19-gaming-pc-fmv-design.md).
 *
 *   parent_fmv = Sum(child_fmv_med x childWeights[child_class]) x buildOverheadMultiplier
 *
 * The roll-up is deliberately NOT renormalized over the children present: a
 * missing child contributes 0, so a parent with only some children degrades to
 * the weighted subset (spec "Primary FMV: sum-of-parts"). A child whose class
 * has no entry in `childWeights` does not contribute.
 */
export interface CompositeRollupForClass {
  /** Per child asset_class weight applied to that child's fmv_med_usd. */
  childWeights: Record<AssetClassId, number>;
  /**
   * Flat factor folding in the parts that are not priced individually
   * (motherboard / PSU / case / cooling). Stored on the snapshot as
   * config_multiplier (numeric(5,4), so must be < 10).
   */
  buildOverheadMultiplier: number;
}

/**
 * SIL-1832: how the condition multiplier treats a baseline that is already a
 * band-specific `sku_mcmp` median.
 *  - 'legacy' (also: field absent): baseline x the asset band's multiplier,
 *    even when the baseline row IS that band's own median (double discount).
 *  - 'band_aware': see `resolveBaselineAwareConditionMultiplier` (fmv-resolution.ts).
 */
export type ConditionBaselineMode = 'legacy' | 'band_aware';

/**
 * Guarded fixed uplift for a `new` asset that has NO `new`-band sku_mcmp
 * baseline (SIL-1831). Its own constant - deliberately NOT derived from the
 * condition table (an inverse band multiplier was evaluated and rejected: it
 * compounds the existing double discount and is unsafe on unknown/high_wear).
 * A class with no entry here has NO fallback: a `new` asset with no
 * `new`-band baseline is refused (422 no_new_band_baseline).
 */
export interface NewBandFallbackRule {
  /**
   * Donor bands in PRIORITY order. A donor row qualifies only if its band is
   * listed here AND sample_size >= minSampleSize AND the SKU's comps are
   * recent. unknown / high_wear / defective are never listed.
   */
  donorBands: ConditionBand[];
  minSampleSize: number;
  /**
   * low/default/high bounds of the uplift. `default` is the factor applied to
   * the donor median; low/high are the owner-approved bounds (recorded in the
   * formula history) - the factor actually applied is stored on the snapshot
   * (baseline_provenance), so affected assets can be found and re-priced.
   */
  uplift: { low: number; default: number; high: number };
  /** Points removed from the data_completeness confidence component when the uplift applies. */
  dataCompletenessPenalty: number;
}

export interface FmvFormulaConfig {
  /**
   * SIL-1832. OPTIONAL and omitted from the default config on purpose: an
   * absent key is not part of the content hash, so `defaultFmvFormulaConfig.version`
   * is unchanged. Setting it (to either value) yields a new `version`.
   */
  conditionBaselineMode?: ConditionBaselineMode;
  ageCurves: Record<AssetClassId, AgeBand[]>;
  conditionMultipliers: Record<AssetClassId, Partial<Record<ConditionBand, ConditionMultiplierBand>>>;
  marketability: Record<AssetClassId, MarketabilityForClass>;
  friction: Record<AssetClassId, FrictionRule[]>;
  /**
   * Sum-of-parts roll-up config, keyed by the PARENT asset class. Lives here
   * (not in a DB table) so the weights are content-hashed into `version` and
   * stamped on fmv_pricing_snapshot.formula_config_version like every other
   * formula input (ADR-0006, Audit-Lock reproducibility).
   */
  compositeRollup: Record<AssetClassId, CompositeRollupForClass>;
  /** SIL-1831: guarded fixed uplift for a `new` asset with no `new`-band baseline, keyed by asset class. */
  newBandFallbackUplift: Record<AssetClassId, NewBandFallbackRule>;
  /** The telemetry factor — composed from telemetry-multiplier.config.ts. */
  telemetry: TelemetryMultiplierConfig;
  /**
   * SIL-1833: optional ask->executed baseline correction. ABSENT = 'disclose_only'
   * (no price change); absent keys are not hashed, so the default `version` is
   * unaffected. See ask-calibration.ts.
   */
  askCalibration?: AskCalibrationConfig;
  /** Deterministic content hash of every field above. See config-hash.ts. */
  readonly version: string;
}

// ---------------------------------------------------------------------------
// Validation — replaces the DB no-overlap guarantees lost by moving off GiST.
// ---------------------------------------------------------------------------

/** Thrown when a formula config violates a structural invariant. */
export class FmvFormulaConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FmvFormulaConfigError';
  }
}

/** True when `n` has no more than 4 decimal places (DB stores numeric(_,4)). */
function isAtMost4dp(n: number): boolean {
  return Number.isFinite(n) && Math.round(n * 10000) === n * 10000;
}

/**
 * Validates the invariants the DB used to enforce that the TS type shape does
 * NOT already guarantee:
 *  - age brackets per class: non-overlapping (gaps allowed — the DB tolerated
 *    them; an age landing in a gap falls back exactly as before). GiST
 *    `age_curve_no_overlap`.
 *  - friction per class: no duplicate (componentId, regionId). GiST
 *    `friction_cost_rule_no_overlap`, collapsed to duplicate-key since lot is
 *    dropped.
 *  - all multiplier/factor/USD values ≤ 4 decimals (numeric(_,4)/(_,2)).
 *
 * Condition-band uniqueness and marketability single-active-row are already
 * guaranteed structurally by the `Partial<Record<...>>` / keyed-object shapes,
 * so they need no runtime check.
 *
 * Throws FmvFormulaConfigError on the first violation.
 */
export function validateFmvFormulaConfig(config: Omit<FmvFormulaConfig, 'version'>): void {
  if (config.askCalibration) {
    const violation = askCalibrationConfigViolation(config.askCalibration);
    if (violation) throw new FmvFormulaConfigError(`askCalibration: ${violation}`);
  }

  for (const [cls, bands] of Object.entries(config.ageCurves)) {
    const sorted = [...bands].sort((a, b) => a.ageMonthsMin - b.ageMonthsMin);
    for (const b of sorted) {
      if (b.ageMonthsMax <= b.ageMonthsMin) {
        throw new FmvFormulaConfigError(
          `age curve for "${cls}": bracket [${b.ageMonthsMin},${b.ageMonthsMax}) has max <= min`,
        );
      }
      if (!isAtMost4dp(b.multiplier)) {
        throw new FmvFormulaConfigError(`age curve for "${cls}": multiplier ${b.multiplier} exceeds 4dp`);
      }
    }
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1]!;
      const cur = sorted[i]!;
      if (cur.ageMonthsMin < prev.ageMonthsMax) {
        throw new FmvFormulaConfigError(
          `age curve for "${cls}": brackets [${prev.ageMonthsMin},${prev.ageMonthsMax}) and [${cur.ageMonthsMin},${cur.ageMonthsMax}) overlap`,
        );
      }
    }
  }

  for (const [cls, bands] of Object.entries(config.conditionMultipliers)) {
    for (const [band, v] of Object.entries(bands)) {
      if (!v) continue;
      if (!(v.low <= v.default && v.default <= v.high)) {
        throw new FmvFormulaConfigError(
          `condition multiplier for "${cls}"/"${band}": requires low <= default <= high (got ${v.low}/${v.default}/${v.high})`,
        );
      }
      for (const [k, n] of Object.entries(v)) {
        if (!isAtMost4dp(n)) {
          throw new FmvFormulaConfigError(`condition multiplier for "${cls}"/"${band}": ${k} ${n} exceeds 4dp`);
        }
      }
    }
  }

  for (const [cls, m] of Object.entries(config.marketability)) {
    const values = [m.classDefault, ...Object.values(m.skuOverrides ?? {})];
    for (const v of values) {
      if (!isAtMost4dp(v.factor)) {
        throw new FmvFormulaConfigError(`marketability for "${cls}": factor ${v.factor} exceeds 4dp`);
      }
    }
  }

  for (const [cls, rules] of Object.entries(config.friction)) {
    const seen = new Set<string>();
    for (const r of rules) {
      const key = `${r.componentId}|${r.regionId ?? '__ANY__'}`;
      if (seen.has(key)) {
        throw new FmvFormulaConfigError(
          `friction for "${cls}": duplicate rule for component "${r.componentId}" region "${r.regionId ?? 'any'}"`,
        );
      }
      seen.add(key);
      if (!isAtMost4dp(r.perUnitUsd) || !isAtMost4dp(r.fixedUsd)) {
        throw new FmvFormulaConfigError(`friction for "${cls}"/"${r.componentId}": USD value exceeds 4dp`);
      }
    }
  }

  for (const [cls, rollup] of Object.entries(config.compositeRollup)) {
    const weights = Object.entries(rollup.childWeights);
    if (weights.length === 0) {
      throw new FmvFormulaConfigError(`composite roll-up for "${cls}": childWeights is empty`);
    }
    for (const [childCls, w] of weights) {
      if (childCls === cls) {
        throw new FmvFormulaConfigError(`composite roll-up for "${cls}": a class cannot be its own child`);
      }
      if (!Number.isFinite(w) || w < 0 || w > 1) {
        throw new FmvFormulaConfigError(
          `composite roll-up for "${cls}": weight for child "${childCls}" must be within [0,1] (got ${w})`,
        );
      }
      if (!isAtMost4dp(w)) {
        throw new FmvFormulaConfigError(`composite roll-up for "${cls}": weight for child "${childCls}" ${w} exceeds 4dp`);
      }
    }
    if (!weights.some(([, w]) => w > 0)) {
      throw new FmvFormulaConfigError(`composite roll-up for "${cls}": at least one child weight must be > 0`);
    }
    const o = rollup.buildOverheadMultiplier;
    // config_multiplier is numeric(5,4): 0 < o < 10.
    if (!Number.isFinite(o) || o <= 0 || o >= 10) {
      throw new FmvFormulaConfigError(
        `composite roll-up for "${cls}": buildOverheadMultiplier must be > 0 and < 10 (got ${o})`,
      );
    }
    if (!isAtMost4dp(o)) {
      throw new FmvFormulaConfigError(`composite roll-up for "${cls}": buildOverheadMultiplier ${o} exceeds 4dp`);
    }
  }

  for (const [cls, rule] of Object.entries(config.newBandFallbackUplift)) {
    const u = rule.uplift;
    if (!(u.low > 0 && u.low <= u.default && u.default <= u.high)) {
      throw new FmvFormulaConfigError(
        `new-band fallback for "${cls}": requires 0 < low <= default <= high (got ${u.low}/${u.default}/${u.high})`,
      );
    }
    for (const [k, n] of Object.entries(u)) {
      if (!isAtMost4dp(n)) {
        throw new FmvFormulaConfigError(`new-band fallback for "${cls}": ${k} ${n} exceeds 4dp`);
      }
    }
    if (rule.donorBands.length === 0 || rule.donorBands.includes('new')) {
      throw new FmvFormulaConfigError(`new-band fallback for "${cls}": donorBands must be non-empty and must not include "new"`);
    }
    if (!Number.isInteger(rule.minSampleSize) || rule.minSampleSize < 1) {
      throw new FmvFormulaConfigError(`new-band fallback for "${cls}": minSampleSize must be an integer >= 1`);
    }
    if (!Number.isFinite(rule.dataCompletenessPenalty) || rule.dataCompletenessPenalty < 0) {
      throw new FmvFormulaConfigError(`new-band fallback for "${cls}": dataCompletenessPenalty must be >= 0`);
    }
  }
}

/**
 * Builds an `FmvFormulaConfig` with `version` derived from the rest via
 * `hashConfig`, after validating structural invariants. Use this instead of a
 * raw object literal so `version` stays in sync with content and an invalid
 * config fails fast at construction.
 */
export function defineFmvFormulaConfig(input: Omit<FmvFormulaConfig, 'version'>): FmvFormulaConfig {
  validateFmvFormulaConfig(input);
  return { ...input, version: hashConfig<FmvFormulaConfig>(input) };
}

// ---------------------------------------------------------------------------
// Default config — reproduces the active v1.0 DB rows (prototype-seed.sql).
// Any change here is a reviewed formula change: it is the FMV formula history.
// ---------------------------------------------------------------------------

export const defaultFmvFormulaConfig: FmvFormulaConfig = defineFmvFormulaConfig({
  ageCurves: {
    gpu: [
      { ageMonthsMin: 0, ageMonthsMax: 72, multiplier: 0.85 },
      { ageMonthsMin: 72, ageMonthsMax: 240, multiplier: 0.5 },
    ],
    server: [{ ageMonthsMin: 0, ageMonthsMax: 72, multiplier: 0.78 }],
    ib_switch: [{ ageMonthsMin: 0, ageMonthsMax: 120, multiplier: 0.72 }],
    // ethernet_switch / router (SIL-1606): mirror ib_switch plus a tail to 1200
    // months so no age can miss (a miss throws `no_age_curve`, which the
    // bootstrap treats as a skip - the first Techills assets sat unpriced).
    ethernet_switch: [
      { ageMonthsMin: 0, ageMonthsMax: 120, multiplier: 0.72 },
      { ageMonthsMin: 120, ageMonthsMax: 1200, multiplier: 0.5 },
    ],
    router: [
      { ageMonthsMin: 0, ageMonthsMax: 120, multiplier: 0.72 },
      { ageMonthsMin: 120, ageMonthsMax: 1200, multiplier: 0.5 },
    ],
    storage: [{ ageMonthsMin: 0, ageMonthsMax: 120, multiplier: 0.65 }],
    // cpu (SIL-1151): FIRST-PASS values for a CPU child under a gaming_pc.
    // Without an entry here resolveMultipliersBootstrap throws `no_age_curve`
    // (a BootstrapError, so the bootstrap sweep silently skips) and the CPU
    // child never gets a first snapshot - the roll-up would drop it forever.
    // The band runs to 1200 months (no gap) so no CPU age can miss it. GPU-like
    // shape: consumer CPUs hold value less well past ~6 years.
    cpu: [
      { ageMonthsMin: 0, ageMonthsMax: 72, multiplier: 0.85 },
      { ageMonthsMin: 72, ageMonthsMax: 1200, multiplier: 0.5 },
    ],
    // gaming_pc (SIL-1149): NEUTRAL by design. A gaming_pc parent is priced as a
    // sum of its children's fmv_med (each already age/condition/marketability-
    // adjusted under its own class), so re-applying age/condition/marketability
    // here would double-count. The entries exist so an unconfigured-class
    // fallback can never zero or garble the parent FMV on any recompute path.
    gaming_pc: [{ ageMonthsMin: 0, ageMonthsMax: 1200, multiplier: 1.0 }],
    // desktop (SIL-2075): FIRST-PASS values for a desktop PC priced from its own
    // SKU comps (unlike gaming_pc this is a LEAF class - no children to carry the
    // age/condition/marketability, so the neutral gaming_pc values would be
    // wrong). Shape copied from cpu (SIL-1151): commercial desktops turn over on
    // a 4-6 year lease cycle and hold value less well past ~6 years. The band
    // runs to 1200 months so no age can miss it (a miss throws `no_age_curve`,
    // which the bootstrap treats as a skip and the asset sits unpriced).
    desktop: [
      { ageMonthsMin: 0, ageMonthsMax: 72, multiplier: 0.85 },
      { ageMonthsMin: 72, ageMonthsMax: 1200, multiplier: 0.5 },
    ],
  },
  conditionMultipliers: {
    gpu: {
      like_new: { low: 0.95, default: 0.98, high: 1.0 },
      normal_enterprise_wear: { low: 0.8, default: 0.85, high: 0.9 },
      high_wear: { low: 0.5, default: 0.6, high: 0.7 },
      unknown: { low: 0.5, default: 0.6, high: 0.7 },
    },
    server: {
      // SIL-1822: neutral. A `new` asset's baseline is the exact-band `new`
      // sku_mcmp row (resolveBootstrapCore: exact ?? mcmpRows[0]), already a
      // new-condition price, so a discount would double-count.
      new: { low: 1.0, default: 1.0, high: 1.0 },
      like_new: { low: 0.92, default: 0.96, high: 1.0 },
      normal_enterprise_wear: { low: 0.75, default: 0.82, high: 0.88 },
      high_wear: { low: 0.45, default: 0.55, high: 0.65 },
      unknown: { low: 0.45, default: 0.55, high: 0.65 },
    },
    ib_switch: {
      like_new: { low: 0.9, default: 0.94, high: 0.98 },
      normal_enterprise_wear: { low: 0.72, default: 0.8, high: 0.86 },
      high_wear: { low: 0.45, default: 0.55, high: 0.65 },
      unknown: { low: 0.45, default: 0.55, high: 0.65 },
    },
    // ethernet_switch / router (SIL-1606): copy of ib_switch's bands.
    ethernet_switch: {
      like_new: { low: 0.9, default: 0.94, high: 0.98 },
      normal_enterprise_wear: { low: 0.72, default: 0.8, high: 0.86 },
      high_wear: { low: 0.45, default: 0.55, high: 0.65 },
      unknown: { low: 0.45, default: 0.55, high: 0.65 },
    },
    router: {
      like_new: { low: 0.9, default: 0.94, high: 0.98 },
      normal_enterprise_wear: { low: 0.72, default: 0.8, high: 0.86 },
      high_wear: { low: 0.45, default: 0.55, high: 0.65 },
      unknown: { low: 0.45, default: 0.55, high: 0.65 },
    },
    storage: {
      like_new: { low: 0.9, default: 0.94, high: 0.98 },
      normal_enterprise_wear: { low: 0.72, default: 0.8, high: 0.86 },
      high_wear: { low: 0.45, default: 0.55, high: 0.65 },
      unknown: { low: 0.45, default: 0.55, high: 0.65 },
    },
    memory: {
      like_new: { low: 0.9, default: 0.94, high: 0.98 },
      normal_enterprise_wear: { low: 0.72, default: 0.8, high: 0.86 },
      high_wear: { low: 0.45, default: 0.55, high: 0.65 },
      unknown: { low: 0.45, default: 0.55, high: 0.65 },
    },
    // cpu (SIL-1151): first-pass, see the ageCurves note. CPUs wear far less than
    // GPUs (no fans/VRM abuse), so the used-vs-new spread is tighter than gpu's.
    cpu: {
      like_new: { low: 0.95, default: 0.98, high: 1.0 },
      normal_enterprise_wear: { low: 0.85, default: 0.9, high: 0.95 },
      high_wear: { low: 0.6, default: 0.7, high: 0.8 },
      unknown: { low: 0.6, default: 0.7, high: 0.8 },
    },
    // gaming_pc (SIL-1149): neutral - see the ageCurves note above.
    gaming_pc: {
      like_new: { low: 1.0, default: 1.0, high: 1.0 },
      normal_enterprise_wear: { low: 1.0, default: 1.0, high: 1.0 },
      high_wear: { low: 1.0, default: 1.0, high: 1.0 },
      unknown: { low: 1.0, default: 1.0, high: 1.0 },
    },
    // desktop (SIL-2075): first-pass, see the ageCurves note. Copy of the
    // storage / memory / ib_switch bands (commodity enterprise IT hardware).
    // No `new` band, like router: a `new` desktop fails closed
    // (no_condition_multiplier) rather than pricing on an unreviewed number.
    desktop: {
      like_new: { low: 0.9, default: 0.94, high: 0.98 },
      normal_enterprise_wear: { low: 0.72, default: 0.8, high: 0.86 },
      high_wear: { low: 0.45, default: 0.55, high: 0.65 },
      unknown: { low: 0.45, default: 0.55, high: 0.65 },
    },
  },
  marketability: {
    gpu: { classDefault: { tier: 'A', factor: 1.0 } },
    server: { classDefault: { tier: 'B', factor: 0.95 } },
    ib_switch: { classDefault: { tier: 'B', factor: 0.95 } },
    // ethernet_switch / router (SIL-1606): copy of ib_switch.
    ethernet_switch: { classDefault: { tier: 'B', factor: 0.95 } },
    router: { classDefault: { tier: 'B', factor: 0.95 } },
    storage: { classDefault: { tier: 'C', factor: 0.9 } },
    memory: { classDefault: { tier: 'C', factor: 0.9 } },
    // cpu (SIL-1151): first-pass, see the ageCurves note.
    cpu: { classDefault: { tier: 'B', factor: 0.95 } },
    // gaming_pc (SIL-1149): neutral factor - see the ageCurves note above.
    gaming_pc: { classDefault: { tier: 'B', factor: 1.0 } },
    // desktop (SIL-2075): first-pass, see the ageCurves note. Tier C / 0.90 like
    // storage and memory: high-volume off-lease supply, thin resale margin.
    desktop: { classDefault: { tier: 'C', factor: 0.9 } },
  },
  friction: {
    gpu: [
      { componentId: 'wipe', regionId: null, perUnitUsd: 25.0, fixedUsd: 0.0 },
      { componentId: 'testing', regionId: null, perUnitUsd: 35.0, fixedUsd: 0.0 },
      { componentId: 'logistics', regionId: null, perUnitUsd: 60.0, fixedUsd: 150.0 },
    ],
    server: [
      { componentId: 'wipe', regionId: null, perUnitUsd: 40.0, fixedUsd: 0.0 },
      { componentId: 'logistics', regionId: null, perUnitUsd: 250.0, fixedUsd: 400.0 },
    ],
  },
  // Sum-of-parts roll-up (SIL-1149). FIRST-PASS values (spec "Open questions"):
  // GPU-dominant weights and a flat build-overhead factor, to be recalibrated
  // once real gaming-PC FMV data exists. Any change is a reviewed formula
  // change (new `version`). The cpu weight takes effect with the CPU child
  // (SIL-1151, cpu age/condition/marketability entries above); memory's is
  // pre-declared and takes effect once RAM children are wired (SIL-1152).
  // A gaming_pc with no CPU child rolls up from its GPU child alone
  // (0.70 x 1.30 = 0.91 x gpu); a CPU child adds cpu_fmv x 0.20 x 1.30.
  compositeRollup: {
    gaming_pc: {
      childWeights: { gpu: 0.7, cpu: 0.2, memory: 0.1 },
      buildOverheadMultiplier: 1.3,
    },
  },
  // SIL-1831 (owner decision 2026-10-11): server only. A `new` server with no
  // `new`-band sku_mcmp row is priced from a like_new / normal_enterprise_wear
  // baseline x 1.10 (bounds 1.00-1.20). Rationale: the server age multiplier
  // (0.78) applies from month 0 and the used baseline already embeds
  // depreciation, so depreciation is counted twice for a sealed unit (a full
  // offset would be 1/0.78 = 1.28); used comps are asking prices, which run
  // above sale prices and pull the other way. 1.10 is the net. Evidence is one
  // SKU (Dell R660 2x5416S 256GB: used ask median $29,575; new component sum
  // $26,240-$31,138; 29,575 x 1.10 x 0.78 = $25,375 vs an independently
  // defended $24k-$28k) - NOT a fitted value. Every applied factor is recorded
  // in fmv_pricing_snapshot.baseline_provenance for later re-pricing.
  newBandFallbackUplift: {
    server: {
      donorBands: ['like_new', 'normal_enterprise_wear'],
      minSampleSize: 3,
      uplift: { low: 1.0, default: 1.1, high: 1.2 },
      dataCompletenessPenalty: 15,
    },
  },
  telemetry: defaultTelemetryMultiplierConfig,
});

// ---------------------------------------------------------------------------
// Candidate-config builder — RUF-441 (what-if preview of an uncommitted change).
// ---------------------------------------------------------------------------

/** Targeted per-class overrides the ops FMV tester can preview before a PR. */
export interface FmvFormulaClassOverride {
  ageCurve?: AgeBand[];
  conditionMultipliers?: Partial<Record<ConditionBand, ConditionMultiplierBand>>;
  marketability?: MarketabilityValue;
  friction?: FrictionRule[];
}

/**
 * Returns a NEW committed-config-derived candidate that applies `override` to a
 * single asset class, re-validated and re-hashed to a fresh `version`. Used by
 * the fmv-preview route so an analyst can see what a formula change would do
 * before merging the formula PR. Pure; does not mutate `base`.
 */
export function applyFormulaClassOverride(
  base: FmvFormulaConfig,
  assetClassId: AssetClassId,
  override: FmvFormulaClassOverride,
): FmvFormulaConfig {
  const next: Omit<FmvFormulaConfig, 'version'> = {
    ageCurves: { ...base.ageCurves },
    conditionMultipliers: { ...base.conditionMultipliers },
    marketability: { ...base.marketability },
    friction: { ...base.friction },
    compositeRollup: base.compositeRollup,
    newBandFallbackUplift: base.newBandFallbackUplift,
    telemetry: base.telemetry,
    ...(base.askCalibration ? { askCalibration: base.askCalibration } : {}),
  };

  if (override.ageCurve) next.ageCurves[assetClassId] = override.ageCurve;
  if (override.conditionMultipliers) {
    next.conditionMultipliers[assetClassId] = {
      ...next.conditionMultipliers[assetClassId],
      ...override.conditionMultipliers,
    };
  }
  if (override.marketability) {
    next.marketability[assetClassId] = {
      ...next.marketability[assetClassId],
      classDefault: override.marketability,
    };
  }
  if (override.friction) next.friction[assetClassId] = override.friction;

  return defineFmvFormulaConfig(next);
}
